// Conversions date ↔ champs de formulaire, en heure LOCALE (module PUR).
//
// Le piège que ce module existe pour éviter : `toISOString()` convertit en UTC,
// alors que `<input type="date">` et `<input type="datetime-local">` affichent
// et relisent en heure LOCALE. Pré-remplir un champ avec `toISOString()` montre
// donc une heure décalée du fuseau (2 h en France l'été), et re-sauvegarder
// réécrit cette valeur fausse : la dérive est CUMULATIVE à chaque aller-retour.
//
// Bug réel en prod sur `fundedAt` : saisi 18:43, réaffiché 16:43, ce qui
// classait en "Challenge" des trades postérieurs au passage funded.
//
// Nommé `resetDateInput` car né du flux de reset, mais utilisé pour tout champ
// date/heure de formulaire.

/**
 * Date → valeur d'un `<input type="date">` ("YYYY-MM-DD").
 *
 * Passe par les composantes LOCALES et non par `toISOString()`, qui convertit
 * en UTC : pour un utilisateur à UTC-5, un reset daté du 21 août à 20h locale
 * deviendrait le 22 août, et le champ pré-remplirait un jour trop tard.
 *
 * Retourne "" sur une entrée invalide, ce que l'input rend comme un champ vide
 * (et la confirmation est alors bloquée côté composant).
 */
export function toDateInputValue(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Valeur d'un `<input type="date">` → Date à MINUIT LOCAL.
 *
 * `new Date("2026-08-21")` est interprété par la spec comme minuit **UTC**,
 * alors que `new Date("2026-08-21T00:00:00")` (sans `Z`) donne minuit local.
 * La distinction compte : la borne de reset étant un `>=`, un décalage de
 * quelques heures peut basculer les trades du jour du reset du mauvais côté.
 *
 * Retourne null si la valeur est vide ou non parsable.
 */
export function fromDateInputValue(value: string): Date | null {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Date → valeur d'un `<input type="datetime-local">` ("YYYY-MM-DDTHH:mm").
 *
 * Même piège que `toDateInputValue`, en plus visible : ici l'heure est affichée,
 * donc un `toISOString().slice(0, 16)` montre franchement la mauvaise heure.
 * C'est le bug qui a faussé `fundedAt` en prod (18:43 saisi, 16:43 réaffiché,
 * puis 14:43 après une nouvelle sauvegarde).
 *
 * Retourne "" sur une entrée invalide.
 */
export function toDateTimeInputValue(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Valeur d'un `<input type="datetime-local">` → Date.
 *
 * `new Date("2026-07-29T18:43")` est déjà interprété en heure locale par la
 * spec (pas de suffixe `Z`), donc la conversion est directe. La fonction existe
 * surtout pour la symétrie avec `toDateTimeInputValue` et pour garantir un
 * `null` propre sur une saisie vide ou invalide, plutôt qu'une `Invalid Date`
 * qui filerait jusqu'au serveur.
 */
export function fromDateTimeInputValue(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}
